import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { User, Mail, Phone, Building2, MapPin, Lock, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientAuth = () => {
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '',
    company: '', city: '', state: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      if (mode === 'register') {
        await axios.post(`${API_URL}/api/clients/register`, form);
        setMessage({ type: 'success', text: 'Cadastro realizado com sucesso! Faça login para continuar.' });
        setMode('login');
      } else {
        const res = await axios.post(`${API_URL}/api/clients/login`, {
          email: form.email, password: form.password
        });
        localStorage.setItem('client', JSON.stringify(res.data.client));
        setMessage({ type: 'success', text: 'Login realizado!' });
        setTimeout(() => navigate('/'), 1000);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao processar';
      setMessage({ type: 'error', text: detail });
    } finally {
      setLoading(false);
    }
  };

  const states = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#055509] py-4">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white hover:text-[#FFB025] transition-colors">
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Voltar ao Site</span>
          </button>
          <span className="text-white font-bold">TerraHydro</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-[#055509]">
              {mode === 'login' ? 'Acesse sua Conta' : 'Crie sua Conta'}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {mode === 'login' ? 'Faça login para acompanhar seus pedidos' : 'Cadastre-se para solicitar orçamentos'}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input data-testid="client-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Seu nome" className="pl-10" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input data-testid="client-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(00) 00000-0000" className="pl-10" required />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="client-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="seu@email.com" className="pl-10" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha *</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="client-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Mínimo 6 caracteres" className="pl-10" required minLength={6} />
                </div>
              </div>

              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                    <div className="relative">
                      <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input data-testid="client-company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Nome da empresa" className="pl-10" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                      <Input data-testid="client-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Cidade" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                      <select data-testid="client-state" value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="w-full border rounded-md px-3 py-2 text-sm">
                        <option value="">UF</option>
                        {states.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {message && (
                <div data-testid="auth-message" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message.type === 'success' ? <Check size={16} /> : null}
                  {message.text}
                </div>
              )}

              <Button data-testid="client-submit-btn" type="submit" className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white py-5" disabled={loading}>
                {loading ? 'Aguarde...' : mode === 'login' ? 'Entrar' : 'Cadastrar'}
              </Button>
            </form>

            <div className="text-center mt-4">
              <button
                data-testid="toggle-auth-mode"
                type="button"
                className="text-sm text-[#055509] hover:underline"
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setMessage(null); }}
              >
                {mode === 'login' ? 'Não tem conta? Cadastre-se' : 'Já tem conta? Faça login'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientAuth;
